import type { Gif } from '../../gifs/types/gif.types'

export interface UseCaseAnalytics {
  useCase: string
  count: number
  percentage: number
  gifIds: string[]
}

export interface UseCaseData {
  totalUseCases: number
  uniqueUseCases: number
  totalGifs: number
  analytics: UseCaseAnalytics[]
}

export class UseCaseAnalyticsService {
  /**
   * Analyze use cases from all GIF documents
   * @param gifs Array of all GIFs
   * @returns Aggregated use case analytics
   */
  static analyzeUseCases(gifs: Gif[]): UseCaseData {
    const useCaseMap = new Map<string, string[]>()

    // Aggregate use cases from all GIFs
    gifs.forEach((gif) => {
      ;(gif.useCases ?? []).forEach((useCase) => {
        if (!useCaseMap.has(useCase)) {
          useCaseMap.set(useCase, [])
        }
        useCaseMap.get(useCase)!.push(gif.id)
      })
    })

    // Calculate total use cases across all GIFs (not unique)
    const totalUseCases = gifs.reduce(
      (sum, gif) => sum + (gif.useCases ?? []).length,
      0
    )

    // Convert to analytics format and sort by frequency
    const analytics: UseCaseAnalytics[] = Array.from(useCaseMap.entries())
      .map(([useCase, gifIds]) => ({
        useCase,
        count: gifIds.length,
        percentage:
          totalUseCases > 0 ? (gifIds.length / totalUseCases) * 100 : 0,
        gifIds,
      }))
      .sort((a, b) => b.count - a.count)

    return {
      totalUseCases,
      uniqueUseCases: useCaseMap.size,
      totalGifs: gifs.length,
      analytics,
    }
  }

  /**
   * Filter analytics by minimum occurrence threshold
   * @param data Use case analytics data
   * @param minCount Minimum number of occurrences to include
   * @returns Filtered analytics
   */
  static filterByMinCount(data: UseCaseData, minCount: number): UseCaseData {
    const filteredAnalytics = data.analytics.filter(
      (item) => item.count >= minCount
    )

    return {
      ...data,
      analytics: filteredAnalytics,
    }
  }

  /**
   * Search use cases by text
   * @param data Use case analytics data
   * @param searchTerm Search term to filter by
   * @returns Filtered analytics matching search term
   */
  static searchUseCases(data: UseCaseData, searchTerm: string): UseCaseData {
    if (!searchTerm.trim()) {
      return data
    }

    const lowerSearchTerm = searchTerm.toLowerCase()
    const filteredAnalytics = data.analytics.filter((item) =>
      item.useCase.toLowerCase().includes(lowerSearchTerm)
    )

    return {
      ...data,
      analytics: filteredAnalytics,
    }
  }

  /**
   * Get summary statistics for use cases
   * @param data Use case analytics data
   * @returns Summary statistics
   */
  static getSummaryStats(data: UseCaseData) {
    if (data.analytics.length === 0) {
      return {
        averageUseCasesPerGif: 0,
        mostCommonUseCase: null,
        leastCommonUseCase: null,
        averageOccurrences: 0,
      }
    }

    const averageUseCasesPerGif = data.totalUseCases / data.totalGifs
    const mostCommonUseCase = data.analytics[0]
    const leastCommonUseCase = data.analytics[data.analytics.length - 1]
    const averageOccurrences = data.totalUseCases / data.uniqueUseCases

    return {
      averageUseCasesPerGif: Number(averageUseCasesPerGif.toFixed(2)),
      mostCommonUseCase,
      leastCommonUseCase,
      averageOccurrences: Number(averageOccurrences.toFixed(2)),
    }
  }
}
